import { X } from 'lucide-react'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

interface LoginModalProps {
  isOpen: boolean
  onClose: () => void
  onConnect: () => void
}

export const LoginModal: React.FC<LoginModalProps> = ({
  isOpen,
  onClose,
  onConnect,
}) => {
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')

  if (!isOpen) return null

  const validateEmail = (emailToValidate: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailToValidate)
  }
  const mockCredentials = {
    email: 'test@cleverlink.org',
    password: 'cleverlink123',
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!validateEmail(email)) {
      setError(t('login.invalidEmail'))
      return
    }

    if (
      email !== mockCredentials.email ||
      password !== mockCredentials.password
    ) {
      setError(t('login.error'))
      return
    }

    onConnect()
    onClose()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-md bg-white/40">
      {/* Gradient background circles for modal, matching homepage */}
      <div className="absolute top-10 left-1/2 -translate-x-1/2 w-60 h-60 bg-pink-300 rounded-full mix-blend-multiply filter blur-3xl opacity-40 animate-pulse"></div>
      <div
        className="absolute bottom-10 right-1/2 translate-x-1/2 w-60 h-60 bg-orange-300 rounded-full mix-blend-multiply filter blur-3xl opacity-40 animate-pulse"
        style={{ animationDelay: '1s' }}
      ></div>
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 relative"
        style={{ backgroundColor: '#faf8f5' }}
      >
        <button
          className="absolute top-4 right-4 text-2xl text-gray-400 hover:text-pink-500 focus:outline-none focus:ring-2 focus:ring-pink-500 flex items-center justify-center bg-transparent z-10"
          onClick={onClose}
          aria-label={t('login.close')}
          type="button"
        >
          <X className="w-6 h-6" />
        </button>
        <div className="mb-6 flex flex-col items-center gap-4">
          <h2 className="text-xl font-semibold">{t('login.header1')}</h2>
          <img
            src="/img/cleverlink-logo-with-typo-color.svg"
            alt="Cleverlink Logo"
          />
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            type="email"
            required
            placeholder={t('login.emailPlaceholder')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring mb-6 focus:border-primary border-gray-300`}
          />
          <input
            type="password"
            required
            placeholder={t('login.passwordPlaceholder')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring focus:border-primary mb-2"
          />
          <div className="flex justify-end mb-4">
            <a
              href="#"
              className="text-xs text-primary hover:underline"
              onClick={(e) => {
                e.preventDefault()
                // TODO: Implement password reset navigation
              }}
            >
              {t('login.forgotPassword')}
            </a>
          </div>
          <button
            type="submit"
            className="w-full py-2 rounded-xl bg-gradient-to-r from-pink-500 to-orange-500 text-white font-bold shadow-lg hover:from-pink-600 hover:to-orange-600 transition"
          >
            {t('login.cta')}
          </button>
          <div className="text-xs text-gray-400 text-center mt-2">
            {t('login.discoverText')}
          </div>
          <div className="text-xs text-center mt-2">
            {t('login.helpText')}{' '}
            <a
              href="mailto:contact@cleverlink.org"
              className="bg-gradient-to-r from-pink-500 to-orange-500 bg-clip-text text-transparent font-semibold hover:underline"
              style={{
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              contact@cleverlink.org
            </a>
          </div>
          {error && (
            <div className="text-sm text-red-500 text-center mt-2">{error}</div>
          )}
        </form>
      </div>
    </div>
  )
}
